import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { useForm } from 'react-hook-form';

import React, { useContext, useEffect, useState } from 'react';

import { SessionContext } from '@contexts/session-context';

import { Session } from '@models/session.model';
import { Tenant } from '@models/tenant.model';

import { TenantService } from '@services/tenant.service';
import { FormData } from '@shared/components/filters/types';

import {
  Button,
  Input,
  Modal,
  useTranslation,
  toastify,
  useInputTextValidationSchema,
  FIELD_NAMES,
  handleFieldUpdate,
} from '@testsigma/atoms';

interface TenantFormProps {
  session: Session;
  isSettingsModalOpen: boolean;
  toggleForm;
}

export const TenantForm: React.FC<TenantFormProps> = ({
  session,
  isSettingsModalOpen,
  toggleForm,
}) => {
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const [tenant, setTenant] = useState<Tenant>(session.tenant);
  const nameDefaultSchema = useInputTextValidationSchema(FIELD_NAMES.NAME);

  const { refreshSession } = useContext(SessionContext);

  const schema = yup.object().shape({
    name: nameDefaultSchema,
  });
  const {
    setValue,
    formState: { errors, isDirty },
    watch,
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: session?.tenant?.company,
    },
  });

  useEffect(() => {
    setTenant(session.tenant);
  }, [session.tenant]);

  const onSubmit = () => {
    setIsSaving(true);
    const updatedTenant = { ...tenant, company: watch('name') };
    TenantService.update(session.user.id, updatedTenant)
      .then((_res) => {
        setTenant(_res as Tenant);
        toastify(
          t('common.message.update.success', { fieldName: `Account Info` }),
          {
            type: 'success',
            position: 'top-center',
          },
        );
        toggleForm?.(false);
        refreshSession();
      })
      .catch((err) => {
        toastify(
          t('common.message.update.failure', { fieldName: `Account Info` }),
          {
            type: 'error',
            position: 'top-center',
          },
        );
      })
      .finally(() => setIsSaving(false));
  };

  return (
    <Modal isOpen={isSettingsModalOpen} className='!rounded-lg'>
      <div className='text-xl leading-5 font-medium'>
        {t('tenant.form_title')}
      </div>
      <Input
        id='wait-time'
        label={t('tenant.form_label')}
        value={watch('name')}
        hasError={Boolean(errors?.name)}
        errorText={errors?.name?.message || ''}
        onChange={(value: string) =>
          handleFieldUpdate({ setValue, fieldName: 'name', value })
        }
        wrapperClassName='w-full mt-4'
        inputClassName='!py-3'
      />
      <div className='flex flex-row-reverse mt-2'>
        <Button
          type='primary'
          onClick={() => onSubmit()}
          isDisabled={
            !!errors?.name || !watch('name')?.length || isSaving || !isDirty
          }
        >
          {t('common.update')}
        </Button>
        <Button
          type='secondary'
          className='mr-3'
          onClick={() => toggleForm(false)}
        >
          {t('common.cancel')}
        </Button>
      </div>
    </Modal>
  );
};